import { signal } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { ExerciseStat, FitnessState } from '@cf-shared/data-model/user-state';
import { Equipment, EquipmentType, EquipmentManual, getPossibleWeights } from '@cf-shared/data-model/equipment';
import { ExerciseSlot, Exercise } from 'services/exercise-db.service';
import { Log_WorkoutSet, Log_WorkoutExercise, Log_WorkoutMiniSet } from '@cf-shared/data-model/log-workout';
import { SetStats } from '@cf-shared/data-model/set-stats';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { IntensityLevel, Plan_ExercisePath, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { WeightSetup } from '@cf-shared/data-model/equipment';
import { UserDb } from 'services/user-db.service';
import { ExerciseStatusBadgeType } from 'components/cs-exercise-status-badge/cs-exercise-status-badge';
import { SetEditSnapshot } from 'pages/workout-detail/set-edit-snapshot';
import { BadgeColor } from 'components/cs-badge/cs-badge';
import { getEquipmentForExercise, getUnitWithLocaleFallback } from 'services/equipment-helpers';
import { equipmentIdForUnitOrUsableLegacy } from 'services/exercise-stat-equipment';
import { resolveUsableCustomWeightIncrement_kg, resolveCustomWeightIncrementSource, reportHealedCustomWeightIncrementIfNeeded, SilentProblemReporter, PendingCustomIncrementHeal } from 'util/custom-weight-increment';
import { LocaleSettings } from '@cf-shared/shared-util';
import { SetDetailInfo } from 'components/cs-log-detail-popover/cs-log-detail-popover';
import { TranslatedString } from 'models/translated-string';
import type { RepCeilingProposal } from 'pages/workout-detail/workout-progression-decision';
import type { WeightAndReps } from 'pages/workout-detail/set-progress-display';

/**
 * Badge data for exercise display. Defined here (not in workout-loader.service.ts, its only
 * builder) because UiExercise.badges below is the field that actually carries it -- a single
 * definition avoids the two having to be retyped together by hand every time this shape changes.
 */
export interface ExerciseBadge {
  label: TranslatedString;
  color: BadgeColor;
  icon?: string;
  info?: TranslatedString;
  helpId?: string;
}

export function generatePossibleWeightValues(equipment: Equipment): number[] {
  let possibleWeights = getPossibleWeights(equipment);
  return possibleWeights.map(e => e.weight_kg);
}

// id prefix for synthetic Manual equipment built from a single inferred custom
// increment. producer: checkEquipment below; consumer: workout-detail label rendering.
export const AUTO_CUSTOM_INCREMENT_EQUIPMENT_ID_PREFIX = 'automatic_custom_increment_';

/** append each path in `candidates` that `paths` does not already hold, compared by identity */
function addPathsIfMissing(paths: Plan_ExercisePath[], candidates: Plan_ExercisePath[]): void {
  for (const path of candidates) {
    if (!paths.includes(path)) {
      paths.push(path);
    }
  }
}

/** append each container's path for `intensity` that `paths` does not already hold. an absent
 *  container, or one with no path for that intensity, adds nothing */
function addPathsForIntensity(
  paths: Plan_ExercisePath[],
  intensity: IntensityLevel,
  containers: (Plan_ExercisePath[] | undefined)[],
): void {
  for (const container of containers) {
    const path = container?.find(p => p.intensityLevel === intensity);
    if (path) {
      addPathsIfMissing(paths, [path]);
    }
  }
}

export class UiSet {
  // Signals for properties that change during user interaction
  instructionsText = signal<string | undefined>(undefined);
  // if true, this instruction is a generic hint (e.g. "try to achieve target reps") that can be hidden for experienced users
  isBasicInstruction = signal(false);
  requireStrengthInput = signal(false);
  requireRepsInput = signal(false);
  inEditMode = signal(false);
  /** whether set 1's compact feedback row is unfolded (#4142 follow-up); page memory only, never stored */
  progressUnfolded = signal(false);

  // Medium priority signals (set once per set initialization, used in templates)
  lastAttemptDays = signal<string | undefined>(undefined);
  lastAttemptValue = signal<string | undefined>(undefined);
  showBarbellHint = signal(false);
  showBwHint = signal(false);

  // Non-signal properties (set once or rarely changed)
  instructionsTextFollowup?: string;
  logSet: Log_WorkoutSet;
  weightSetup?: WeightSetup;
  setupTextParts: { label: string; value: string }[];
  setupErrorWeight?: string;
  // #6151: set while the weight field shows the equipment's lightest weight because the plan's
  // target is lighter than the equipment can go. Display only; worked out in weightSetupToText.
  equipmentFloorHint?: { text: TranslatedString; title: TranslatedString; explanation: TranslatedString };
  // #6197: the added weight `openSet` worked out for a deload, drop or rep-max follow-up set from an
  // earlier set, before rounding it onto the equipment. Display only, never stored on `logSet`: the
  // floor hint compares it with the equipment's lightest weight. Reset each time the set is opened.
  derivedWeight_kg?: number;
  // #6197: true while this set sits on the equipment's lightest weight because the lighter weight it
  // worked out is below it; kept as it was once the set is logged. Display only: picks the drop-set
  // banner's wording.
  heldAtEquipmentFloor?: boolean;
  uiExercise: UiExercise;
  weightBeforeEdit?: number;
  repsBeforeEdit?: number;
  miniSetsBeforeEdit?: Log_WorkoutMiniSet[];
  /** #6694: everything a reopened done set recorded when its edit began; the collapse arrow puts it back */
  editSnapshot?: SetEditSnapshot;
  /**
   * #4556: the weight and reps the strength figures currently stored on this set were calculated
   * from. Every place that runs `estimate1RM` on a real set — as opposed to a throwaway clone — records
   * it here, so a later edit can tell
   * whether those figures still match what the set records. Absent means nothing has recalculated
   * them during this page's lifetime, so the values the edit started from are what they came from.
   */
  derivedStatsFrom?: { weight_kg: number; reps: number };
  /**
   * #5979: what to offer the user because this set reached the rep ceiling, written by `targetHit`
   * and asked by `concludeSet` once the progression is decided. Only lives for one conclusion.
   */
  pendingRepCeiling?: RepCeilingProposal;
}

export class UiExercise {
  exercise: Exercise;
  description: string;
  // True when description carries an intensity classification ("high-rep" /
  // "heavy" / "medium"). Drives the inline periodization-intensity ⓘ in
  // cs-exercise-list — without this, the icon would render next to past-mode
  // log strings, "X sets" template strings, and "skipped".
  hasPeriodizationDescription = false;
  stat: ExerciseStat;
  equipment: Equipment;
  // #6295: a stored step `checkEquipment` replaced in memory, waiting for the workout page to save
  // it. Set and cleared by every `checkEquipment` call; this model never writes the database itself.
  pendingCustomIncrementHeal?: PendingCustomIncrementHeal;
  planPath?: Plan_ExercisePath;

  sets: UiSet[];
  openSetIndex = signal(-1);

  tutorialSeen = false;
  equipmentPreselected = false;
  slot: ExerciseSlot;
  planSubSlot: Plan_SubSlot;
  /** The id under which the user's program holds `planSubSlot` above, written down when the
   *  row is built rather than searched for later. Set and read by the Edit Workouts page
   *  only — the workout-detail page keeps its id on `logExercise.subSlotId` instead. */
  planSubSlotId?: string;
  /** #4614: true when the user's program no longer holds an entry under this exercise's
   *  sub-slot id — they removed the exercise, and `planSubSlot` above was restored from the
   *  snapshot the workout log carries so a finished session still shows what was performed.
   *  Everything that writes an edited sub-slot back into `plan.subSlots` must skip when this
   *  is set: writing would put back an entry the program deliberately dropped, which the
   *  plan's own garbage collector then deletes again while raising a false alarm. */
  planSubSlotMissingFromPlan = false;
  /** #4939: true when `planSubSlot` above is this session's own historical snapshot, chosen on
   *  purpose because the workout is finished, rather than a copy of the entry the user's
   *  program holds today. The program may well still hold that entry — this is NOT the #4614
   *  case above, and the two are deliberately separate: that one says the entry is GONE, this
   *  one says the object we are holding is OLD.
   *
   *  What it changes: nothing may write this object into the user's program, because doing so
   *  would push a finished session's settings over the ones the user trains with now. Writes
   *  go to `livePlanSubSlot` instead. See `canWritePlanSubSlotBackToPlan()` below. */
  planSubSlotIsHistorical = false;
  /** #6428 R9: true once the page has seen that the program's entry for this exercise carries a
   *  different coach stamp than this session's own snapshot (`coachChangedSinceSnapshot`): a
   *  coach (or the user, through the exercise editor) changed the exercise after this session
   *  concluded its first set. It stays true for the rest of the page's life. While it is set the
   *  session writes nothing into the program's entry: `canWritePlanSubSlotBackToPlan()` refuses,
   *  the page leaves `livePlanSubSlot` unset, and the page raises no new question about the
   *  exercise. See `design-docs/coach-exercise-editing.md`, "Mid-workout". */
  coachChangedDuringSession = false;
  /** The FITNESS PLAN's own entry for this exercise — the object that gets saved into
   *  `currentplan` — or undefined when there is no entry to write into. NOT the same object as
   *  `planSubSlot` above, which is a deep COPY, until `writeSubSlotBackToPlan` has attached it.
   *  The PAGE owns keeping this current. Why it is a separate container, why it is a write target
   *  and never a replacement, and when the page refreshes it:
   *  `design-docs/volume-autoregulation.md`, "The containers a path lives in". */
  livePlanSubSlot?: Plan_SubSlot;
  /** every sub-slot id this exercise has been renamed AWAY from during this page's life.
   *  Switching to an exercise in a different slot mints a new sub-slot and renames
   *  `logExercise.subSlotId` in place, so an id that used to name this entry can still be
   *  sitting in the stored document on a copy taken before the switch. The save's merge
   *  drops those rather than keeping them beside the renamed entry — see
   *  `mergeScreenExercisesOverStored`. Empty for the overwhelming majority of exercises. */
  retiredSubSlotIds: string[] = [];
  /** df-148d4d4ced87: the set objects an exercise change moved off this entry into a split-off
   *  one (#5507) during this page's life. A failed switch can leave them on screen while the
   *  entry's own list is empty, and `fillMissingEarlierSetsFromScreen` must not copy them back:
   *  the split-off entry already holds them. Never stored. */
  setsSplitOffBySwitch = new Set<Log_WorkoutSet>();
  logExercise: Log_WorkoutExercise;

  /** Stats from set 1 of the most recent completed workout for this exercise+intensity.
   *  Used by concludeSet() for progression decisions instead of stale planPath.lastStats. */
  previousFirstSetStats?: SetStats;

  /** The strongest finished set, failed or not, of the most recent session at this intensity in which
   *  the exercise was really done (a skipped session is looked past). A first set must beat it to be
   *  praised as "You progressed!" (#6447, `previousSessionBestAttempt`). Display only; filled by the
   *  same background read as `previousFirstSetStats`. */
  previousAttemptStats?: SetStats;

  // Reduced target weight after layoff: the fraction taken off (0.025 or 0.05), or -1 when the
  // break was long enough to force a strength retest. Drives the toast and the conclusion's writes.
  reducedTargetWeight_pc?: number;
  // Layoff fallback: when a weight cut would not show on the equipment, the bucket is applied to
  // the rep target instead and the weight is held. Mutually exclusive with
  // `reducedTargetWeight_pc` at population time.
  reducedTargetReps_delta?: number;
  /** #5617: the weight the layoff decided for the path being performed, BEFORE the #5176 cap. The
   *  first set's conclusion stores exactly this as the path's target weight, in both the weight-cut
   *  and the rep-cut branch, so what the user was asked for and what the plan remembers agree.
   *  Never recomputed at conclusion (the rep cut has lowered the rep target by then) and never taken
   *  from `targetWeight_kg` (which can hold the cap, deliberately never stored). Undefined when the
   *  layoff did not apply, or applied under the older last-lifted-weight rule. */
  layoffWeight_kg?: number;
  /** #5617: the evidence set's one-rep max (bodyweight share included) after the layoff cut. The
   *  conclusion runs it back to each OTHER intensity path's own rep target. Set with
   *  `layoffWeight_kg`. */
  layoffCut1RM_net_kg?: number;
  resetProgress?: boolean;
  targetWeight_kg?: number;

  /** Set by the loader when it lowered `targetWeight_kg` because the plan asked for a weight the
   *  user's own recent performance says they cannot lift for the prescribed reps (#5176). Carries
   *  the unsnapped ceiling too, which is NEGATIVE when the reps are impossible even unloaded — the
   *  page reports that case rather than letting the zero floor hide it. Also carries the weight the
   *  plan wanted before the cap, so the page can snap the cap again once it has attached equipment
   *  the load could not see. */
  prescriptionCap?: { cappedWeight_kg: number; ceiling_kg: number; plannedWeight_kg: number };

  /** Set by the loader when this exercise's previous session at this intensity ended in an accepted
   *  deload that lowered the target weight one step (#4822). The page then tells the user why the
   *  weight is lower, and that the set is a normal working set rather than a light deload set. */
  deloadCarriedOver?: boolean;

  // Temporary exercise swap (session only, plan not modified)
  isTempSwap?: boolean;
  tempSwapOriginalExerciseId?: string;

  loading = signal(false);

  // Combo set grouping (undefined = individual exercise, will be populated later)
  comboSetNumber?: number;

  // Cached badges to prevent re-rendering every frame
  badges?: ExerciseBadge[];

  // Cached myoreps state from when sets were built (used to detect config changes)
  builtWithMyoReps?: boolean;

  // Last workout info for this exercise (e.g., "Tue, Dec 10 : 175lb × 5, 4, 3")
  lastWorkoutInfo = signal<string | undefined>(undefined);

  // Last workout detailed data for popover/expanded view
  lastWorkoutSets = signal<SetDetailInfo[]>([]);
  lastWorkoutDate = signal<string | undefined>(undefined);
  // last workout's sets as one string with no planned-weight brackets, e.g. "65 kg × 11, 5", for the
  // compact finished-exercise summary (#4142 follow-up)
  lastWorkoutSetsText = signal<string | undefined>(undefined);
  // last workout's weight and reps per set index, for "Last session" on an open set and "Last time"
  // in a done set's details (#4142 review fixes, R4); an entry is undefined when that set is unusable
  lastWorkoutSetStats = signal<(WeightAndReps | undefined)[]>([]);
  lastWorkoutExpanded = signal(false);
  lastWorkoutHasNotes = signal(false);
  // True while the last-workout history query is in flight. The query is fired
  // off the critical path (it can take seconds on large DBs / during backfill),
  // so the card shows a spinner in this section until it resolves.
  lastWorkoutLoading = signal(false);
  // The newest last-workout query started for this exercise, kept so that concluding a first set
  // can wait for the `previousFirstSetStats` it fills (df-3a4a75620292). Never rejects.
  lastWorkoutRead?: Promise<void>;
  // True while a Done tap waits for `lastWorkoutRead`. Nothing is concluded yet during that wait,
  // so a second tap meanwhile is dropped, or both taps would conclude the same set.
  isDoneTapAwaitingPreviousSession = false;

  // Edit mode description (e.g., "Hip Extension | 3 Sets | RM Testing | Myo-Reps")
  editDescription?: string;

  // Computed status methods for cs-exercise-list display
  isCompleted(): boolean {
    return this.sets.every(s => s.logSet &&
      (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped));
  }

  isIncomplete(): boolean {
    return this.sets.some(s => s.logSet &&
        (s.logSet.state === LogRecordState.Done || s.logSet.state === LogRecordState.Skipped)) &&
      this.sets.some(s => s.logSet &&
        (s.logSet.state === LogRecordState.Incomplete || s.logSet.state === LogRecordState.Scheduled));
  }

  isSkipped(): boolean {
    return this.sets.some(s => s.logSet && s.logSet.state === LogRecordState.Skipped);
  }

  getStatusBadgeType(): ExerciseStatusBadgeType {
    if (this.isCompleted()) return ExerciseStatusBadgeType.Complete;
    if (this.isIncomplete()) return ExerciseStatusBadgeType.InProgress;
    return ExerciseStatusBadgeType.Number;
  }

  checkEquipment(fitnessState: FitnessState, userDb: UserDb, locale: LocaleSettings, logging: SilentProblemReporter) {
    void userDb;

    // if we have custom increment defined without equipment
    const exerciseStat = fitnessState.exerciseStats[this.exercise._id];
    const currentUnit = getUnitWithLocaleFallback(this.stat?.weight_unit, locale);
    // a legacy machine configured in the other unit does not count, the same as in
    // getEquipmentForExercise below (df-0caf8c4d43d4)
    const hasEquipmentForUnit = equipmentIdForUnitOrUsableLegacy(exerciseStat, currentUnit, fitnessState.allEquipment);

    // #5344: the weight a step has to be able to load, when the exercise has any history. The
    // freshest source wins — `previousFirstSetStats` is the strongest successful set of the most recent completed workout,
    // and `planPath.lastStats` can be older than it. Left undefined for an exercise with no history,
    // which makes the plausibility rules judge roundness alone. Computed HERE, above the choice
    // between the two units, because that choice needs it too: without it a stored 80 kg step on an
    // exercise trained at 80 kg looks round enough to win, and the genuine other-unit step beside it
    // is thrown away before the heal ever sees it.
    const workingWeight_kg = this.previousFirstSetStats?.weight_kg ?? this.planPath?.lastStats?.weight_kg;
    // decide which stored per-unit custom increment to use — see resolveCustomWeightIncrementSource's
    // own doc comment for the full preference order (current unit, then other unit, then whichever
    // unit has anything recorded at all, healed below if corrupt — #4337)
    const otherUnit = currentUnit === 'lb' ? 'kg' : 'lb';
    const incrementSource = resolveCustomWeightIncrementSource(
      exerciseStat?.customWeightIncrementByUnit?.[currentUnit],
      exerciseStat?.customWeightIncrementByUnit?.[otherUnit],
      workingWeight_kg,
    );
    const customIncrement = incrementSource?.increment_kg;
    const effectiveUnit = incrementSource?.fromOtherUnit ? otherUnit : currentUnit;
    // re-derived on every call, so a heal that no longer applies is never saved later
    this.pendingCustomIncrementHeal = undefined;

    if (exerciseStat && customIncrement !== undefined && !hasEquipmentForUnit) {
      // the equipment unit this synthetic equipment displays and steps in — NOT necessarily the same
      // key the stored increment lives under (see storedIncrementUnit below), since this falls back
      // to effectiveUnit only when this.stat has no weight_unit of its own
      const equipmentWeightUnit = this.stat?.weight_unit || effectiveUnit;
      const normalizedEquipmentUnit: 'kg' | 'lb' = equipmentWeightUnit === 'lb' ? 'lb' : 'kg';
      // the actual customWeightIncrementByUnit key the (possibly corrupt) value came from — used only
      // for the canary below, so a debugger looking at production data checks the right field
      const storedIncrementUnit: 'kg' | 'lb' = effectiveUnit === 'lb' ? 'lb' : 'kg';
      // Heal a stored value that is already corrupt: report it once per exercise+unit per session,
      // then substitute a usable step. #4337 caught a 0, negative or NaN; #5344 widened it to any
      // step that describes no real machine. This read-time heal does NOT write the repair back
      // itself. It records it in `pendingCustomIncrementHeal`, and the workout page saves it (#6295),
      // because only a caller with a working weight can recognise the whole-weight family and the
      // boot migration `repairImplausibleCustomWeightIncrements` has none. See
      // design-docs/reactive-deload.md's "#4337" section.
      reportHealedCustomWeightIncrementIfNeeded(
        this.exercise._id, storedIncrementUnit, customIncrement, logging, workingWeight_kg);
      const usableIncrement = resolveUsableCustomWeightIncrement_kg(
        customIncrement, normalizedEquipmentUnit, workingWeight_kg);
      // if the heal replaced the stored value, keep the value this screen actually uses, so what the
      // user saw is what gets saved
      if (!Object.is(usableIncrement, customIncrement)) {
        this.pendingCustomIncrementHeal = {
          exerciseId: this.exercise._id,
          unit: storedIncrementUnit,
          storedIncrement_kg: customIncrement,
          healedIncrement_kg: usableIncrement,
        };
      }
      // create a temporary EquipmentManual object for custom increment
      this.equipment = {
        id: AUTO_CUSTOM_INCREMENT_EQUIPMENT_ID_PREFIX + this.exercise._id,
        name: LL.workouts.detail.equipment.automatic(),
        type: EquipmentType.Manual,
        weight_unit: equipmentWeightUnit,
        weight_min_kg: 0,
        weight_inc_kg: usableIncrement
      } as EquipmentManual;
    } else {
      // regular equipment lookup
      this.equipment = getEquipmentForExercise(fitnessState, this.stat, locale);
    }

    if (this.equipment) {
      this.possibleWeightValues_kg = generatePossibleWeightValues(this.equipment);
    }
  }

  /**
   * #4939: may the whole `planSubSlot` object be written into the user's program?
   *
   * No in two separate situations, which are not the same thing and are recorded separately:
   * the program no longer holds an entry under this exercise's sub-slot id (`#4614`, the entry
   * is GONE), or this exercise is being shown from a finished session's own snapshot
   * (`#4939`, the object is OLD). Writing in either case puts settings into the program that
   * the user did not ask for — a resurrected entry the plan's garbage collector deletes again
   * while raising an alarm, or an old session's targets over the ones being trained today.
   *
   * This is about the WHOLE object only. A progression write for the current intensity still
   * reaches the program through `livePlanSubSlot`, which points at the plan's own entry.
   *
   * #6428 adds a third: someone changed the exercise through the editor during this session
   * (`coachChangedDuringSession`). Writing would put the session's copy over their change.
   */
  canWritePlanSubSlotBackToPlan(): boolean {
    return !this.planSubSlotMissingFromPlan && !this.planSubSlotIsHistorical && !this.coachChangedDuringSession;
  }

  /**
   * Every live exercise-path object that a progression write for this exercise's CURRENT intensity
   * has to reach, each returned once, `planPath` first. Callers loop over it and write their fields
   * once per entry; writing through `planPath` alone leaves the other containers stale, which is
   * right in memory and wrong after a reload.
   *
   * It cannot see a path belonging to a DIFFERENT workout — write that one separately.
   *
   * Full rules (why there are three containers, which one `planPath` points into, and what the
   * backup is for): `design-docs/volume-autoregulation.md`, "The containers a path lives in".
   */
  livePathsForCurrentIntensity(): Plan_ExercisePath[] {
    if (!this.planPath) {
      return [];
    }

    const intensity = this.planPath.intensityLevel;
    const paths: Plan_ExercisePath[] = [this.planPath];

    // `livePlanSubSlot` is the plan's OWN entry. While `planSubSlot` is still the loader's
    // detached copy it is a distinct object and has to be written separately; once
    // `writeSubSlotBackToPlan` has attached the copy the two are the same object and the
    // identity check below drops the duplicate, so the attached case is unchanged (#4890's rule,
    // extended by df-8d1620a9593c).
    // if a container's path is the very object planPath already points at, it is already in the list
    // #6428: while a coach change guards this exercise, the program entry is never written
    addPathsForIntensity(paths, intensity, [
      this.planSubSlot.exercisePaths,
      this.coachChangedDuringSession ? undefined : this.livePlanSubSlot?.exercisePaths,
      this.logExercise.planSubslotBackup?.exercisePaths,
    ]);

    return paths;
  }

  /**
   * Every live exercise-path object an ALL-INTENSITY progression sweep has to write: each
   * intensity's path in the plan sub-slot, plus the other containers that hold the CURRENT
   * intensity's path. Each object is returned once.
   *
   * Why the current intensity needs the extra entries. A sweep that walks
   * `planSubSlot.exercisePaths` alone writes the PAGE's copy of the intensity being performed and
   * leaves the workout's own copy (`logExercise.planSubslotBackup`) holding the pre-sweep value.
   * `WorkoutLoaderService.setCurrentPath` prefers that copy once it exists, so the very next time
   * it runs — the user re-opens the exercise, or the app reloads mid-workout — `planPath` points
   * at the stale one and the page shows the prescription the sweep replaced. See
   * `design-docs/volume-autoregulation.md`, "The containers a path lives in", which settled
   * (#4531) that the workout's own copy carries that workout's own progression.
   *
   * "The other containers" is whatever `livePathsForCurrentIntensity()` reaches, so since
   * `df-8d1620a9593c` that includes the FITNESS PLAN's own entry when the page is working on a
   * detached copy. A sweep therefore reaches the plan for the intensity being performed even
   * then, which is the same repair that finding made for the single-intensity writes.
   *
   * The OTHER intensities get the page's copy and the fitness plan's own entry, but NOT the
   * workout's own copy — see `pageAndPlanPathsForIntensity()` for why (df-e5bc0aa664f1).
   */
  sweepPathsForEveryIntensity(): Plan_ExercisePath[] {
    const paths: Plan_ExercisePath[] = [];

    for (const pagePath of this.planSubSlot.exercisePaths) {
      addPathsIfMissing(paths, this.pageAndPlanPathsForIntensity(pagePath.intensityLevel));
    }
    addPathsIfMissing(paths, this.livePathsForCurrentIntensity());

    return paths;
  }

  /**
   * df-e5bc0aa664f1: the live path objects a write to an intensity this workout is NOT performing
   * has to reach — the page's copy of that intensity's path, then the fitness plan's own one, each
   * returned once. The rep-target push onto a neighbouring intensity uses it, and so do the sweeps.
   *
   * The plan's entry is needed for the same reason as in `livePathsForCurrentIntensity()`: a page
   * rebuilt mid-workout holds a detached copy, so a write that reaches only the copy is lost from
   * the saved plan. Once the copy is attached the two are one object and this returns just one.
   *
   * The workout's own copy is deliberately left out. Nothing reads a non-performed intensity out of
   * it except a finished session's own display, where "what this session started with" is the
   * honest record.
   *
   * The plan's entry is also left out while this exercise is shown from a finished session's own
   * snapshot (`planSubSlotIsHistorical`). Every number a neighbour push is decided from is read off
   * that snapshot, so writing it into the live program would overwrite the targets the user trains
   * with today using an old session's values. Full rules: `design-docs/volume-autoregulation.md`,
   * "The rule, settled by #4890".
   *
   * #5276 added the one exception: a finished session that still OWNS the exercise's progression
   * writes the live entry after all. See `ownsLiveProgression` for why that is not the same risk.
   */
  pageAndPlanPathsForIntensity(intensity: IntensityLevel): Plan_ExercisePath[] {
    const paths: Plan_ExercisePath[] = [];
    const planContainer = (this.planSubSlotIsHistorical && !this.ownsLiveProgression) || this.coachChangedDuringSession
      ? undefined
      : this.livePlanSubSlot?.exercisePaths;
    addPathsForIntensity(paths, intensity, [this.planSubSlot.exercisePaths, planContainer]);
    return paths;
  }

  expanded = signal(false);
  highlighted = signal(false);

  startedInFuture?: boolean;
  /**
   * #5276: this FINISHED session still owns the exercise's progression, so a conclusion on it may
   * reach the live program for every intensity — not only the one it performed.
   *
   * The ownership rule is #4939's and lives in `util/historical-subslot.ts`: the exercise's most
   * recent concluded session owns live progression, until the first set of the NEXT session of
   * that exercise is concluded. `WorkoutDetailPage.onClickSetDone` computes the answer before it
   * concludes anything, sets this for the duration of that one conclusion, and clears it in a
   * `finally` — so it can never be left standing for a later tap.
   *
   * It is read in two places. `pageAndPlanPathsForIntensity` decides whether a write to an intensity
   * this session did NOT perform reaches the user's program. The reason it needs to: a strength test
   * derives a target for every intensity from one estimate, so without this a corrected test moved
   * only the intensity that session trained and left the others on targets derived from the wrong
   * result. `afterSessionSnapshotFor` (`pages/workout-detail/autoregulation-resolve.ts`) re-takes
   * the session's "as it left the exercise" copy from the live program entry, so a later layoff
   * restore does not bring back progression the correction removed (df-5dbef4889250).
   *
   * It deliberately does NOT unlock `canWritePlanSubSlotBackToPlan()`. That would write the whole
   * sub-slot — the old session's set count, exercise choice and training level — over the live
   * entry, which is the failure `design-docs/exercise-replacement.md` records under "The general
   * rule, why it was withdrawn once". Only the per-intensity path objects are in scope here.
   *
   * False on every workout that is not a finished session's own snapshot, where it is never read.
   */
  ownsLiveProgression = false;
  possibleWeightValues_kg?: number[];
}
